/**
 * One part of a page that may fail on its own: the transcript, a card, a tab,
 * a preview. When it does, it becomes a small card in its place, with Try
 * again and the folded details, and the rest of the page carries on.
 *
 *   import { SectionBoundary } from "@/components/errors";
 *   <SectionBoundary name="deck-preview"><DeckPreview … /></SectionBoundary>
 *
 * `name` marks the card (`data-section`) so a screenshot or a bug report says
 * which part it was. `resetKeys` clears it when what the part shows changes
 * (a job id, a tab). Stale code is offered a Reload here, never made to
 * reload: the page around it may hold unsaved work.
 */
import type { ReactNode } from "react";
import { RefreshCwIcon, RotateCcwIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useErrorCopy } from "./copy";
import { ErrorBoundary, type FallbackProps } from "./error-boundary";
import { ErrorDetails, ReloadingScreen } from "./error-fallback";
import { reloadNow } from "./stale";

export interface SectionBoundaryProps {
  children?: ReactNode;
  /** Which part this is, for `data-section`. */
  name?: string;
  /**
   * Classes for the card that replaces the part: its margin, its height in a pane. It fills its column like any
   * block (so a margin never overflows it); in a flex row, pass `flex-1`.
   */
  className?: string;
  /** The card clears when one of these changes. */
  resetKeys?: readonly unknown[];
  /** Called after "Try again", to refetch what the part shows. */
  onReset?: () => void;
}

export function SectionBoundary({ children, name, className, resetKeys, onReset }: SectionBoundaryProps) {
  return (
    <ErrorBoundary
      autoReload={false}
      resetKeys={resetKeys}
      onReset={onReset}
      reloading={<ReloadingScreen size="section" />}
      fallback={(props) => <SectionFallback {...props} name={name} className={className} />}
    >
      {children}
    </ErrorBoundary>
  );
}

export function SectionFallback({
  error,
  at,
  stale,
  reset,
  name,
  className,
}: FallbackProps & { name?: string; className?: string }) {
  const copy = useErrorCopy();
  return (
    <div
      role="alert"
      data-section={name}
      className={cn(
        "bg-card/60 animate-in fade-in-0 flex min-w-0 items-start gap-3 rounded-xl border border-dashed p-4 text-sm duration-300",
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg",
          stale ? "bg-brand/10 text-brand-ink" : "bg-warning-muted text-warning",
        )}
      >
        {stale ? <SparklesIcon className="size-4" /> : <TriangleAlertIcon className="size-4" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="font-medium">{stale ? copy.staleTitle : copy.sectionTitle}</p>
          <p className="text-muted-foreground text-pretty">{stale ? copy.staleBody : copy.sectionBody}</p>
        </div>
        <div className="flex flex-col items-start gap-2">
          {stale ? (
            <Button size="sm" onClick={reloadNow}>
              <RefreshCwIcon data-icon="inline-start" />
              {copy.reload}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={reset}>
              <RotateCcwIcon data-icon="inline-start" />
              {copy.tryAgain}
            </Button>
          )}
          <div className="-ml-1.5 w-full">
            <ErrorDetails error={error} at={at} align="start" />
          </div>
        </div>
      </div>
    </div>
  );
}
